import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../config/firebase';
import { verifyAdminToken } from '../services/adminAuth';
import { useTheme } from '../context/ThemeContext';
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  AlertCircle,
  LogIn,
  Loader
} from 'lucide-react';
import loginVideo from '../assets/login.mp4';
import { handleSanitizedChange, validateAndSanitize } from '../utils/inputSanitization';

const AdminLogin = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, type = 'text') => (e) => {
    handleSanitizedChange(e, (sanitizedValue) => {
      setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
      if (error) setError('');
    }, type);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate inputs before submission
    const emailValidation = validateAndSanitize(formData.email, 'email');
    if (!emailValidation.valid) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate password is not empty and doesn't contain dangerous patterns
    if (!formData.password || formData.password.trim().length === 0) {
      setError('Password is required');
      return;
    }

    setLoading(true);

    try {
      // Sign in with Firebase - use sanitized email
      const userCredential = await signInWithEmailAndPassword(
        auth,
        emailValidation.sanitized,
        formData.password
      );

      // Get Firebase ID token
      const idToken = await userCredential.user.getIdToken();

      // Verify admin status with backend
      const adminData = await verifyAdminToken(idToken, emailValidation.sanitized);

      if (!adminData) {
        await auth.signOut();
        throw new Error('You do not have admin access');
      }

      // Store admin data
      sessionStorage.setItem('adminData', JSON.stringify(adminData));

      // Navigate to dashboard
      navigate('/admin/dashboard');

    } catch (err) {
      console.error('Login error:', err);
      
      let errorMessage = 'Invalid email or password';
      
      // Don't reveal which field is wrong - security best practice
      if (err.code === 'auth/invalid-email' || 
          err.code === 'auth/user-not-found' || 
          err.code === 'auth/wrong-password' ||
          err.code === 'auth/invalid-credential') {
        errorMessage = 'Invalid email or password';
      } else if (err.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later';
      } else if (err.message && err.message.includes('admin access')) {
        errorMessage = 'Invalid email or password';
      } else if (err.message) {
        // Generic message for any other errors
        errorMessage = 'Login failed. Please check your credentials';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          className="w-full h-full object-cover"
        >
          <source src={loginVideo} type="video/mp4" />
        </video>
        {/* Dark overlay for readability */}
        <div className={`absolute inset-0 ${
          isDarkMode 
            ? 'bg-black/70' 
            : 'bg-black/50'
        }`}></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/fsgu-logo.png" 
            alt="FSGU Logo" 
            className="h-24 w-auto mx-auto mb-4 drop-shadow-2xl"
          />
          <h1 className={`text-3xl font-bold mb-2 drop-shadow-lg ${
            isDarkMode ? 'text-white' : 'text-white'
          }`}>
            Admin Login
          </h1>
          <p className="text-sm text-white/90 drop-shadow-md">
            Free State Golf Union Administration
          </p>
        </div>

        {/* Login Form Card */}
        <div className={`rounded-2xl p-8 shadow-2xl backdrop-blur-xl border ${
          isDarkMode 
            ? 'bg-fsgu-gray-800/90 border-fsgu-gray-700/50' 
            : 'bg-white/90 border-fsgu-silver-200/50'
        }`}>
          {/* Error Message */}
          {error && (
            <div className={`mb-6 p-4 rounded-lg flex items-start space-x-3 ${
              isDarkMode 
                ? 'bg-fsgu-red-900/30 border border-fsgu-red-700/50' 
                : 'bg-fsgu-red-50 border border-fsgu-red-200'
            }`}>
              <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                isDarkMode ? 'text-fsgu-red-400' : 'text-fsgu-red-600'
              }`} />
              <p className={`text-sm ${
                isDarkMode ? 'text-fsgu-red-300' : 'text-fsgu-red-700'
              }`}>
                {error}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div>
              <label 
                htmlFor="email" 
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}
              >
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className={`w-5 h-5 ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                  }`} />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange('email', 'email')}
                  required
                  className={`w-full pl-10 pr-4 py-3 rounded-lg transition-all duration-300 ${
                    isDarkMode
                      ? 'bg-fsgu-gray-700 border border-fsgu-gray-600 text-fsgu-silver-100 placeholder:text-fsgu-gray-400 focus:border-fsgu-green-500 focus:ring-2 focus:ring-fsgu-green-500/20'
                      : 'bg-fsgu-white border border-fsgu-silver-300 text-fsgu-silver-900 placeholder:text-fsgu-silver-400 focus:border-fsgu-green-500 focus:ring-2 focus:ring-fsgu-green-500/20'
                  } focus:outline-none`}
                  placeholder="admin@fsgugolf.co.za"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label 
                htmlFor="password" 
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
                }`}
              >
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className={`w-5 h-5 ${
                    isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-400'
                  }`} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange('password', 'text')}
                  required
                  className={`w-full pl-10 pr-12 py-3 rounded-lg transition-all duration-300 ${
                    isDarkMode
                      ? 'bg-fsgu-gray-700 border border-fsgu-gray-600 text-fsgu-silver-100 placeholder:text-fsgu-gray-400 focus:border-fsgu-green-500 focus:ring-2 focus:ring-fsgu-green-500/20'
                      : 'bg-fsgu-white border border-fsgu-silver-300 text-fsgu-silver-900 placeholder:text-fsgu-silver-400 focus:border-fsgu-green-500 focus:ring-2 focus:ring-fsgu-green-500/20'
                  } focus:outline-none`}
                  placeholder="Enter your password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute inset-y-0 right-0 pr-3 flex items-center transition-colors ${
                    isDarkMode
                      ? 'text-fsgu-silver-500 hover:text-fsgu-silver-300'
                      : 'text-fsgu-silver-400 hover:text-fsgu-silver-600'
                  }`}
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                loading
                  ? isDarkMode
                    ? 'bg-fsgu-gray-700 text-fsgu-silver-500 cursor-not-allowed'
                    : 'bg-fsgu-silver-200 text-fsgu-silver-500 cursor-not-allowed'
                  : isDarkMode
                    ? 'bg-fsgu-green-500 text-white hover:bg-fsgu-green-600 hover:scale-105 shadow-soft hover:shadow-green-glow'
                    : 'bg-fsgu-green-500 text-white hover:bg-fsgu-green-600 hover:scale-105 shadow-soft hover:shadow-green-glow'
              }`}
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Back to Home Link */}
        <div className="mt-6 text-center">
          <a
            href="/"
            className={`text-sm transition-colors duration-300 ${
              isDarkMode
                ? 'text-fsgu-silver-400 hover:text-fsgu-green-400'
                : 'text-fsgu-silver-600 hover:text-fsgu-green-600'
            }`}
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;